import { useState } from "react";
import { TextField } from "../TextFields/TextFields";
import { H3 } from "../Typography/Typography";
import { Form, SubmitButtonContainer } from "../../layout/FormLayout";
import styled from "styled-components/macro";
import type { OptionType } from "../../types/types";
import { isDefined } from "../../types/types";
import { PrimaryButtonFitContainer } from "../Buttons/Buttons";
import { SearchSelectInfiniteScroll } from "../SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { EditableListItem } from "../EditableListItem/EditableListItem";
import type { IFeaturedProductsData } from "./FeaturedProducts";
import { useSaveHomePage } from "../../pages/public/HomePage/useSaveHomePage";
import type { IHomePageData } from "../../pages/public/HomePage/HomePage";
import { useFormWrapper, useStoreState } from "../../util/util";
import { Controller } from "react-hook-form";
import { endpoints } from "../../endpoints";
import { MinimalProductSummary } from "../../pages/SharedPages/OrganizationPage/ProductsList/ProductsList";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-content: space-between;
`;

/**
 * The form for editing the "Featured Products" section in homepage templates.
 *
 * Note: productIds in the homepage data may contain IDs of products that are
 * no longer featured.  See docstring for FeaturedProducts component.
 */
export const FeaturedProductsEditForm = ({
  data: { title },
  maximumProducts,
  featuredProducts,
  homePageData,
  handleEditSubmit,
}: {
  data: IFeaturedProductsData;
  featuredProducts: OptionType<string>[];
  maximumProducts: number;
  homePageData: IHomePageData;
  handleEditSubmit: () => void;
}) => {
  const { saveHomePage } = useSaveHomePage();
  const [loading, setLoading] = useState(false);
  const { tenant_id } = useStoreState();

  const [selectedProducts, setSelectedProducts] =
    useState<OptionType<string>[]>(featuredProducts);

  const productsForDisplay = selectedProducts.filter(isDefined);

  const maxProductsReached = productsForDisplay.length === maximumProducts;

  const { handleSubmit, register, formState, errors, control } = useFormWrapper(
    {
      defaultValues: {
        title,
        featured_products_search: undefined,
      },
    }
  );

  const handleProductMenuSelection = (option: OptionType<string> | null) => {
    if (option && option.value) {
      setSelectedProducts((previous) => [...previous, option]);
    }
  };

  const handleRemoveProduct = (productId: string) => {
    setSelectedProducts((previous) =>
      previous.filter(({ value }) => value !== productId)
    );
  };

  const onSubmit = async (formData: { title: string }) => {
    setLoading(true);

    const newData = {
      title: formData.title,
      productIds: selectedProducts.map(({ value }) => value),
    };

    const component = homePageData.config.components.find(
      (component) => component.name === "featuredProducts"
    );

    if (component) {
      component.data = newData;
    } else {
      homePageData.config.components.push({
        name: "featuredProducts",
        data: newData,
      });
    }

    const success = await saveHomePage(homePageData);
    if (success) {
      handleEditSubmit();
    }
    setLoading(false);
  };

  return (
    <Container>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <TextField
          name="title"
          label="Section Title"
          theref={register({
            required: true,
          })}
          formState={formState}
          errors={errors}
          type="text"
        />

        <div>
          <H3>Featured Products to Display</H3>
          <p>Up to {maximumProducts} products may be displayed.</p>

          {productsForDisplay.map((product) => {
            return (
              <EditableListItem
                key={product.value}
                title={product.label}
                deleteFunction={() => handleRemoveProduct(product.value)}
              />
            );
          })}
        </div>
        {!maxProductsReached && (
          <Controller
            name="featured_products_search"
            control={control}
            render={({ onChange }) => (
              <SearchSelectInfiniteScroll
                name="featured_products_search"
                errors={errors}
                formState={formState}
                placeholder="Featured Products"
                baseUrl={
                  tenant_id
                    ? endpoints.v2_tenants_id_pim_products_list_minimal(
                        tenant_id
                      )
                    : null
                }
                params={
                  new URLSearchParams({
                    order_by: "asc",
                    published_products_only: "true",
                    status: "published",
                  })
                }
                getOptions={(products: MinimalProductSummary[]) =>
                  products
                    .filter(
                      (product) =>
                        !selectedProducts.some(
                          ({ value }) => value === product.id
                        )
                    )
                    .map((product) => ({
                      value: product.id,
                      label: product.name ?? "",
                    }))
                }
                onChange={(option: OptionType<string> | null) => {
                  handleProductMenuSelection(option);
                  onChange(null);
                }}
                value={null}
                showValue={false}
              />
            )}
          />
        )}
        <SubmitButtonContainer>
          <PrimaryButtonFitContainer loading={loading}>
            Save Changes
          </PrimaryButtonFitContainer>
        </SubmitButtonContainer>
      </Form>
    </Container>
  );
};
